import PropTypes from 'prop-types';
import React from 'react';
import EducationDrawer from 'components/EducationDrawer';

// Helper function to get ticket/pass text based on platform
const getTicketText = (count = 1) => {
  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  if (isTicketHoss) {
    return count === 1 ? 'ticket' : 'tickets';
  }
  return count === 1 ? 'pass' : 'passes';
};

/**
 * Education drawer component for credit/refund method selection
 * Explains the difference between full ticket and partial refunds/credits
 */
const CreditMethodEducation = ({
  type,
  isOpen,
  onOpen,
  onClose,
  OptionCard,
  OptionTitle,
  OptionDescription,
  OptionList,
  OptionListItem,
  ImportantNoteBox,
}) => {
  return (
    <EducationDrawer
      title={`Understanding ${type === 'refund' ? 'Refund' : 'Credit'} Options`}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      helpButtonLabel="Learn about refund and credit options"
      helpButtonSize={28}
    >
      <div style={{ marginBottom: '24px' }}>
        <p style={{ fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
          Choose the right {type} option based on your customer's situation.
          Each option serves different purposes and has different effects on{' '}
          {getTicketText(1)} validity.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <OptionCard color="#fa4616">
          <OptionTitle color="#fa4616">
            Full{' '}
            {getTicketText(1).charAt(0).toUpperCase() +
              getTicketText(1).slice(1)}{' '}
            {type === 'refund' ? 'Refund' : 'Credit'}
          </OptionTitle>
          <OptionDescription>
            Select specific {getTicketText(2)} to{' '}
            {type === 'refund' ? 'refund' : 'credit'} completely. The{' '}
            {getTicketText(2)} will be <strong>invalidated</strong> and cannot
            be used for entry.
          </OptionDescription>
          <OptionList>
            <OptionListItem>Customer can no longer attend</OptionListItem>
            <OptionListItem>Event cancellation</OptionListItem>
            <OptionListItem>Duplicate purchases</OptionListItem>
            <OptionListItem>Wrong event selected</OptionListItem>
          </OptionList>
        </OptionCard>

        <OptionCard color="#22c55e">
          <OptionTitle color="#22c55e">
            Partial {type === 'refund' ? 'Refund' : 'Credit'}
          </OptionTitle>
          <OptionDescription>
            Issue a custom amount while keeping {getTicketText(2)}{' '}
            <strong>valid</strong>. Customer retains their {getTicketText(2)}{' '}
            and can still attend.
          </OptionDescription>
          <OptionList>
            <OptionListItem>Service recovery</OptionListItem>
            <OptionListItem>Price adjustments</OptionListItem>
            <OptionListItem>Goodwill gestures</OptionListItem>
            <OptionListItem>Customer complaints</OptionListItem>
            <OptionListItem>Promotional credits</OptionListItem>
          </OptionList>
        </OptionCard>

        <ImportantNoteBox>
          <div
            style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 16 16"
              fill="#fa4616"
              style={{ flexShrink: 0, marginTop: '2px' }}
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
              <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
            </svg>
            <div>
              <p
                style={{
                  margin: '0 0 8px 0',
                  fontSize: '13px',
                  fontWeight: '600',
                }}
              >
                Important Note
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: '13px',
                  lineHeight: '1.5',
                  opacity: 0.8,
                }}
              >
                {type === 'refund'
                  ? 'Refunds are processed back to the original payment method and may take 3-5 business days to appear.'
                  : "Credits are added to the customer's account balance and can be used for future purchases."}
              </p>
            </div>
          </div>
        </ImportantNoteBox>
      </div>
    </EducationDrawer>
  );
};

CreditMethodEducation.propTypes = {
  type: PropTypes.oneOf(['refund', 'credit']).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  OptionCard: PropTypes.elementType.isRequired,
  OptionTitle: PropTypes.elementType.isRequired,
  OptionDescription: PropTypes.elementType.isRequired,
  OptionList: PropTypes.elementType.isRequired,
  OptionListItem: PropTypes.elementType.isRequired,
  ImportantNoteBox: PropTypes.elementType.isRequired,
};

export default CreditMethodEducation;
